import { useState } from 'react';
import { LogIn, Loader2 } from 'lucide-react';
import { startLogin } from '@/lib/oauth';
import { cn } from '@/lib/cn';

interface Props {
  /** Visual variant: solid (primary CTA) or ghost (subtle in nav). */
  variant?: 'solid' | 'ghost';
  /** Return to this path after successful login. Default: current page. */
  returnTo?: string;
  className?: string;
}

/**
 * "Login with BalesOtomatis.id" CTA.
 *
 * Kicks off the OAuth 2.0 Authorization Code flow with PKCE — see
 * /lib/oauth.ts. On click the entire page navigates to
 * auth.balesotomatis.id/oauth/authorize. There's no spinner state to keep
 * around because the page is replaced; we show a brief "Redirecting…"
 * while crypto.subtle.digest runs (a few ms).
 */
export function LoginButton({ variant = 'solid', returnTo, className }: Props) {
  const [loading, setLoading] = useState(false);

  const onClick = async () => {
    if (loading) return;
    setLoading(true);
    try {
      await startLogin({ returnTo });
    } catch (err) {
      console.error('OAuth start failed:', err);
      setLoading(false);
    }
  };

  // Ghost variant: di top-bar, pill bg-elevated. Solid: hero/CTA pill bg-accent.
  // Mobile: icon-only square (icon "Login"). Desktop: full "Login with BalesOtomatis".
  if (variant === 'ghost') {
    return (
      <button
        type="button"
        onClick={onClick}
        disabled={loading}
        className={cn(
          'inline-flex items-center gap-2 h-10 rounded-pill border-2 border-border-strong',
          'px-0 w-10 md:w-auto md:px-4',
          'bg-elevated shadow-brutal-sm hover:shadow-brutal hover:-translate-y-0.5',
          'active:translate-y-0.5 active:shadow-brutal-none transition-all',
          'font-display font-bold text-sm justify-center',
          'disabled:opacity-50 disabled:cursor-wait',
          className
        )}
        title="Login with BalesOtomatis"
      >
        {loading ? (
          <Loader2 size={14} className="animate-spin" />
        ) : (
          <LogIn size={14} />
        )}
        <span className="hidden md:inline">Login with BalesOtomatis</span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loading}
      className={cn(
        'inline-flex items-center gap-2 px-5 h-12 rounded-pill border-[2.5px] border-border-strong',
        'bg-accent shadow-brutal hover:shadow-brutal-lg hover:-translate-y-0.5',
        'active:translate-y-0.5 active:shadow-brutal-sm transition-all',
        'font-display font-bold text-sm text-fg',
        'disabled:opacity-50 disabled:cursor-wait',
        className
      )}
    >
      {loading ? (
        <>
          <Loader2 size={16} className="animate-spin" />
          <span>Redirecting…</span>
        </>
      ) : (
        <>
          <BalesLogoMark />
          <span>Login with BalesOtomatis.id</span>
        </>
      )}
    </button>
  );
}

function BalesLogoMark() {
  // Brutal circle "B" mark — matches the playground logo style.
  return (
    <span
      aria-hidden="true"
      className="inline-flex items-center justify-center w-6 h-6 rounded-pill border-2 border-border-strong bg-base text-fg text-2xs font-display font-extrabold"
    >
      B
    </span>
  );
}
